import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import { confirmResetPassword } from "@/api/auth";
import { AuthenticationScreenContainer, errorToast } from "@/components";
import { Button, PasswordInput } from "@/ui";
import {
  testPasswordContainsNumber,
  testPasswordContainsSpecialCharacter,
  testPasswordContainsUppercaseLetter,
  testPasswordLength,
} from "@/utils";

const schema = z
  .object({ password: z.string(), passwordConfirmation: z.string() })
  .refine(
    (data) => {
      return testPasswordLength(data.password);
    },
    {
      message: "The password is too short",
      path: ["password"],
    },
  )
  .refine(
    (data) => {
      return testPasswordContainsUppercaseLetter(data.password);
    },
    {
      message: "The password must contain a capital letter",
      path: ["password"],
    },
  )
  .refine(
    (data) => {
      return testPasswordContainsNumber(data.password);
    },
    {
      message: "The password must contain a number",
      path: ["password"],
    },
  )
  .refine(
    (data) => {
      return testPasswordContainsSpecialCharacter(data.password);
    },
    {
      message: "The password must contain a special character",
      path: ["password"],
    },
  );

type CreateNewPasswordFormData = z.infer<typeof schema>;

export const ResetPassword = () => {
  const navigate = useNavigate();

  const { email, token } = Route.useSearch();

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<CreateNewPasswordFormData>({
    resolver: zodResolver(schema),
  });

  const { mutate: confirmResetPasswordMutation } = useMutation({
    mutationFn: confirmResetPassword,
    onSuccess: () => {
      navigate({ to: "/password-updated" });
    },
    onError: errorToast,
  });

  const submitPassword = (data: CreateNewPasswordFormData) => {
    if (email && token) {
      confirmResetPasswordMutation({
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
        email,
        token,
      });
    }
  };

  return (
    <AuthenticationScreenContainer className="flex flex-col gap-6">
      <h1 className="text-2xl font-bold">Create new password</h1>

      <p className="text-content-secondary">
        Must be at least 8 characters long and contain both capital & lowercase letters, number, and
        special character
      </p>

      <form className="flex w-full flex-col gap-5" onSubmit={handleSubmit(submitPassword)}>
        <PasswordInput
          className="h-14"
          error={errors.password?.message}
          id="password"
          label="New password"
          showDisplayPasswordButton
          {...register("password")}
        />

        <PasswordInput
          className="h-14"
          error={errors.passwordConfirmation?.message}
          id="password-confirmation"
          label="Confirm new password"
          showDisplayPasswordButton
          {...register("passwordConfirmation")}
        />

        <Button className="w-full" size="lg" type="submit" variant="primary">
          Continue
        </Button>
      </form>
    </AuthenticationScreenContainer>
  );
};

export const Route = createFileRoute("/_guest/reset-password/")({
  beforeLoad: async ({ search }) => {
    if (!search.email || !search.token) {
      throw redirect({ to: "/forgot-password" });
    }
  },
  component: ResetPassword,
  validateSearch: z.object({ email: z.string(), token: z.string() }),
});
